import React, { useEffect, useState } from "react";
import { api, fetchBranding, cachedBranding, brandingLogoUrl, publicBaseUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Copy, CheckCircle2, LogOut, Users } from "lucide-react";

const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

export default function AffiliateHome() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(cachedBranding() || { app_name: "Toko", has_logo: false });
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchBranding().then(setBrand); }, []);
  useEffect(() => {
    if (user?.role === "affiliate") api.get("/affiliate/me").then(({ data }) => setStats(data)).catch(() => setStats({ error: true }));
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-start p-8 text-slate-500">Memuat...</div>;
  if (!user || user.role !== "affiliate") return <Navigate to="/login" replace />;

  const link = stats && !stats.error ? `${publicBaseUrl()}/toko?ref=${stats.code}` : "";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          {brand.has_logo ? (
            <img src={brandingLogoUrl()} alt={brand.app_name} className="w-10 h-10 rounded-xl object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-extrabold">{brand.app_name.slice(0, 2).toUpperCase()}</div>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-extrabold text-slate-900 truncate" style={{fontFamily:'Manrope'}}>Affiliate {brand.app_name}</h1>
            <p className="text-xs text-slate-500">{user.full_name}</p>
          </div>
          <button data-testid="affiliate-logout" onClick={() => { logout(); navigate("/login"); }} className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center"><LogOut size={17} /></button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        <div data-testid="affiliate-link-card" className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center"><Users size={17} /></div>
            <h2 className="font-bold text-lg text-slate-900" style={{fontFamily:'Manrope'}}>Link Affiliate Anda</h2>
          </div>
          {stats === null && <p className="text-sm text-slate-500">Memuat...</p>}
          {stats?.error && <p className="text-sm text-red-600">Gagal memuat data affiliate.</p>}
          {stats && !stats.error && (
            <>
              <p className="text-sm text-slate-600">Kode: <b className="font-mono text-purple-700" data-testid="affiliate-code">{stats.code}</b></p>
              <div className="flex gap-2">
                <input data-testid="affiliate-link" readOnly value={link} className="flex-1 h-11 px-3 rounded-lg border border-slate-300 bg-slate-50 text-xs font-mono" />
                <button data-testid="affiliate-copy-link" onClick={() => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="h-11 px-4 rounded-lg bg-purple-600 text-white text-xs font-bold flex items-center gap-1.5">
                  {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />} {copied ? "Tersalin" : "Salin"}
                </button>
              </div>
              <p className="text-xs text-slate-500">Bagikan link ini — setiap pesanan yang masuk lewat link Anda tercatat otomatis dan komisi dihitung saat pesanan selesai.</p>
            </>
          )}
        </div>

        {stats && !stats.error && (
          <div data-testid="affiliate-stats" className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>{stats.orders}</p><p className="text-[10px] uppercase font-bold text-slate-500">Order</p></div>
            <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-sm font-extrabold text-slate-900 pt-1.5">{fmt(stats.omset)}</p><p className="text-[10px] uppercase font-bold text-slate-500">Omset</p></div>
            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4"><p className="text-sm font-extrabold text-emerald-700 pt-1.5">{fmt(stats.komisi)}</p><p className="text-[10px] uppercase font-bold text-emerald-600">Komisi</p></div>
          </div>
        )}
      </main>
    </div>
  );
}
